// src/components/Cash/CashTable.jsx
// Component to display cash movement data in a table format

import React from 'react';

/**
 * CashTable Component
 * Displays a list of cash movements in a table format
 * 
 * @param {Array} cashMovements - Array of cash movement objects to display
 * @param {boolean} isLoading - Whether data is currently loading
 * @param {string} activeType - Current type filter (CASH, COLLECTION, CHEQUE)
 */
const CashTable = ({ cashMovements, isLoading, activeType }) => {
  /**
   * Format date for display
   * @param {string} dateString - Date string from backend
   */
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  /**
   * Format currency
   * @param {string|number} amount - Amount to format
   */
  const formatCurrency = (amount) => {
    if (!amount) return 'Rs 0.00';
    return `Rs ${parseFloat(amount).toFixed(2)}`;
  };

  // Show loading state while data is being fetched
  if (isLoading) {
    return (
      <div className="inventory-data-table-container">
        <table className="inventory-data-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Inventory</th>
              <th>Sales Person</th>
              <th>Amount</th>
              <th>Description</th>
              <th>Reference</th>
              <th>Reason</th>
              <th>Date</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td colSpan="8" className="cash-loading">
                Loading {activeType.toLowerCase()} data...
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    );
  }

  // Show empty state if no movements match filter
  if (!cashMovements || cashMovements.length === 0) {
    return (
      <div className="inventory-data-table-container">
        <table className="inventory-data-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Inventory</th>
              <th>Sales Person</th>
              <th>Amount</th>
              <th>Description</th>
              <th>Reference</th>
              <th>Reason</th>
              <th>Date</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td colSpan="8" className="cash-empty-state">
                No {activeType.toLowerCase()} records match the current filter.
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    );
  }

  // Render the cash movements table with data
  return (
    <div className="inventory-data-table-container">
      <table className="inventory-data-table">
        <thead>
          <tr>
            <th>ID</th>
            <th>Inventory</th>
            <th>Sales Person</th>
            <th>Amount</th>
            <th>Description</th>
            <th>Reference</th>
            <th>Reason</th>
            <th>Date</th>
          </tr>
        </thead>
        <tbody>
          {cashMovements.map((movement) => (
            <tr key={movement.id}>
              {/* ID - Show last 8 characters */}
              <td className="cash-id-cell">
                {movement.id.slice(-8)}
              </td>
              
              {/* Inventory */}
              <td>
                <div className="cash-inventory-cell">
                  <div className="inventory-name">{movement.inventory?.name || '-'}</div>
                  <div className="inventory-code">{movement.inventory?.code || ''}</div>
                </div>
              </td>
              
              {/* Sales Person */}
              <td>
                <div className="cash-person-cell">
                  <div className="person-name">{movement.salesPerson?.firstName || '-'}</div>
                  <div className="person-email">{movement.salesPerson?.email || ''}</div>
                </div>
              </td>
              
              {/* Amount */}
              <td className="cash-amount-cell">
                {formatCurrency(movement.amount)}
              </td>
              
              {/* Description */}
              <td className="cash-description-cell">
                {movement.description || '-'}
              </td>
              
              {/* Reference */}
              <td>
                {movement.reference || '-'}
              </td>
              
              {/* Reason */}
              <td>
                {movement.reason || '-'}
              </td>
              
              {/* Date */}
              <td className="cash-date-cell">
                {formatDate(movement.date || movement.createdAt)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default CashTable;